import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { 
  CompareSlot, 
  CompareState, 
  CompareViewMode, 
  CompareShowSections, 
  CompareContextValue 
} from '@/types/compare.types';
import { ParsedSunoOutput } from '@/types';

const CompareContext = createContext<CompareContextValue | null>(null);

const DEFAULT_SECTIONS: CompareShowSections = {
  lyrics: true,
  vocals: true,
  production: true,
  metadata: true,
  sunoTags: true
};

// Fix: explicitly allow optional children to fix JSX mapping issue in some TypeScript environments
export const CompareProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CompareState>({
    isActive: false,
    slotA: null,
    slotB: null,
    viewMode: 'split',
    showSections: DEFAULT_SECTIONS,
    syncScroll: true,
    highlightChanges: true
  });

  const setSlotA = useCallback((slot: CompareSlot | null) => {
    setState(prev => ({ ...prev, slotA: slot }));
  }, []);

  const setSlotB = useCallback((slot: CompareSlot | null) => {
    setState(prev => ({ ...prev, slotB: slot }));
  }, []);

  const swapSlots = useCallback(() => {
    setState(prev => ({ ...prev, slotA: prev.slotB, slotB: prev.slotA }));
  }, []);

  const clearComparison = useCallback(() => {
    setState(prev => ({ ...prev, slotA: null, slotB: null }));
  }, []);

  const setViewMode = useCallback((mode: CompareViewMode) => {
    setState(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const toggleSection = useCallback((section: keyof CompareShowSections) => {
    setState(prev => ({
      ...prev,
      showSections: { ...prev.showSections, [section]: !prev.showSections[section] }
    }));
  }, []);

  const setSyncScroll = useCallback((sync: boolean) => {
    setState(prev => ({ ...prev, syncScroll: sync }));
  }, []);

  const setHighlightChanges = useCallback((highlight: boolean) => {
    setState(prev => ({ ...prev, highlightChanges: highlight }));
  }, []);

  const openCompare = useCallback(() => {
    setState(prev => ({ ...prev, isActive: true }));
  }, []);

  const closeCompare = useCallback(() => {
    setState(prev => ({ ...prev, isActive: false }));
  }, []);

  const value: CompareContextValue = {
    ...state,
    setSlotA,
    setSlotB,
    swapSlots,
    clearComparison,
    setViewMode,
    toggleSection,
    setSyncScroll,
    setHighlightChanges,
    openCompare,
    closeCompare
  };

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) throw new Error('useCompare must be used within a CompareProvider');
  return context;
};

export const useCreateCompareSlot = () => {
  const { setSlotA, setSlotB } = useCompare();

  const createSlotFromOutput = useCallback((output: ParsedSunoOutput, id?: string): CompareSlot => {
    return {
      id: id || `slot_${Date.now()}`,
      timestamp: new Date(),
      source: 'generation',
      title: output.title,
      lyrics: output.lyricsWithTags,
      vocalPhysicsBlocks: output.lyricsWithTags.match(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g) || [],
      productionNotes: output.styleBlueprint,
      sunoStyleTags: output.style.split(',').map(t => t.trim()),
      rawOutput: output.fullResponse
    };
  }, []);

  return { createSlotFromOutput, setSlotA, setSlotB };
};